import { shortSpinId } from '../../../Model/Spin/spinId.js'
import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import {
  ArrowTopRightOnSquareIcon,
  ArrowRightIcon,
  ChevronRightIcon,
  ClipboardDocumentIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  ShieldCheckIcon
} from '@heroicons/react/24/outline'
import type { SpinSummary } from '../../../Generated/SpinSummary.ts'
import type { Taxas } from '../../../Model/ITaxa.ts'
import { identityFor, identityKeySvg } from '../../Component/Charts/Options/visualIdentity.js'
import { AmpliconWidget } from '../OrderManagementPage/AmpliconWidget.js'
import { formatDate, parseDate } from '../../../Util/DateHandling.js'
import { LastBlasted } from './LastBlasted.js'
import { gbifRedirectPath } from '../Gbif/gbif.js'
import { BlastSourceIcon } from '../../Component/BlastSource/BlastSourceIcon.js'
import { blastSourceColor, blastSourceDescription } from '../../../Model/Project/blastSourceVisuals.ts'
import {
  RANK_LABELS,
  SPARSE_MEMBER_THRESHOLD,
  agreementAt,
  badgeName,
  cleanName,
  conflict,
  formatScore,
  headline,
  isItalicRank,
  recordLink,
  sourceRows,
  binCountries,
  binSpecies,
  headlineScore,
  hitQuality,
  rdpSupportAt,
  referenceIds,
  reachTone,
  unityTone,
  sourceVersion,
  REACH_WORD,
  RDP_CONFIDENT,
  UNITY_WORD,
  type ScoreTone,
  type SourceRow
} from './spinSummary.logic.js'
import type { SpinConfidence } from '../../../Generated/SpinConfidence.ts'

const SPECIES_RANK = 7

export const Section: React.FC<{ title: string; hint?: string; className?: string; children: React.ReactNode }> = ({
  title,
  hint,
  className = '',
  children
}) => (
  <section className={`bg-white rounded-xl border border-emerald-100 shadow-md p-5 ${className}`}>
    <h2 className={`text-lg font-bold text-gray-900 ${hint ? '' : 'mb-4'}`}>{title}</h2>
    {hint && <p className="text-sm text-gray-600 mb-4">{hint}</p>}
    {children}
  </section>
)

const ExternalLink: React.FC<{ href: string; children: React.ReactNode }> = ({ href, children }) => (
  <a href={href} target="_blank" rel="noopener noreferrer" className="link link-primary inline-flex items-center gap-1">
    {children}
    <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5" />
  </a>
)

/** The chart identity of the SPIN's order as a large swatch, until there are images per taxon; unlabelled so it is not read as an identification. */
const TaxonBadge: React.FC<{ name?: string }> = ({ name }) => (
  <div
    className="w-28 h-28 rounded-xl bg-emerald-50 border border-emerald-100 flex flex-col items-center justify-center shrink-0"
    title={name ? `Chart colour of ${name}` : undefined}
  >
    {name ? (
      <span
        aria-hidden="true"
        // identityKeySvg emits only colours and fixed geometry, never the name.
        dangerouslySetInnerHTML={{ __html: identityKeySvg(identityFor(name), { size: 72, lineLength: 0 }) }}
      />
    ) : (
      <span className="text-gray-300 text-3xl">?</span>
    )}
  </div>
)

/** An ⓘ button whose explanation shows on hover or focus. */
export const InfoTip: React.FC<{ text: string }> = ({ text }) => (
  <span className="tooltip tooltip-bottom before:max-w-xs before:whitespace-normal before:text-left" data-tip={text}>
    <button type="button" aria-label={text} className="align-middle text-gray-400 hover:text-primary">
      <InformationCircleIcon className="w-4 h-4" />
    </button>
  </span>
)

const CONFIDENCE_STYLE: Record<SpinConfidence['band'], string> = {
  HIGH: 'bg-emerald-100 text-emerald-900 border-emerald-300',
  MODERATE: 'bg-amber-100 text-amber-900 border-amber-300',
  LOW: 'bg-rose-100 text-rose-900 border-rose-300'
}

const CONFIDENCE_LABEL: Record<SpinConfidence['band'], string> = {
  HIGH: 'High confidence',
  MODERATE: 'Moderate confidence',
  LOW: 'Low confidence'
}

const ConfidenceBadge: React.FC<{ confidence?: SpinConfidence }> = ({ confidence }) => {
  if (!confidence) return null
  const grade = confidence.grade == null ? 'no BLAST grade' : `best BLAST grade ${confidence.grade.toFixed(1)} (${confidence.gradeSource})`
  const why = `${grade}${confidence.discordant ? '; the databases disagree, which lowers it (to low when they split at order or above)' : ''}. High from grade 97 (the report keeps species), moderate from 90 (keeps family).`
  return (
    <span className="inline-flex items-center gap-1">
      <span className={`badge border gap-1 font-semibold ${CONFIDENCE_STYLE[confidence.band]}`}>
        <ShieldCheckIcon className="w-3.5 h-3.5" />
        {CONFIDENCE_LABEL[confidence.band]}
      </span>
      <InfoTip text={why} />
    </span>
  )
}

const SupersededBanner: React.FC<{ ids?: string[] }> = ({ ids }) =>
  ids && ids.length > 0 ? (
    <div className="flex flex-wrap items-center gap-2 rounded-lg bg-sky-50 border border-sky-200 text-sky-900 text-sm px-3 py-2 lg:col-span-2">
      <ArrowRightIcon className="w-4 h-4" />
      This SPIN has been superseded by
      {ids.map((id) => (
        <Link key={id} to={`/spins/${id}`} className="link font-mono">
          {shortSpinId(id)}
        </Link>
      ))}
    </div>
  ) : null

export const HeroSection: React.FC<{ summary: SpinSummary }> = ({ summary }) => {
  const display = summary.display
  const head = headline(display)
  const split = conflict(display)
  const path = (display?.ranks ?? []).filter((r) => r.value)
  const depth = display?.depth
  const resolvedBy =
    depth == null
      ? undefined
      : display?.ranks?.[depth]?.basis === 'DERIVED'
        ? `from ${display.preferredSource}`
        : agreementAt(display, depth)
  return (
    <section className="bg-white rounded-xl border border-emerald-100 shadow-md p-5 grid gap-4 lg:grid-cols-[1fr_18rem]">
      <SupersededBanner ids={summary.successorSpinIds} />
      <div className="flex gap-4 min-w-0">
        <TaxonBadge name={badgeName(display)} />
        <div className="min-w-0 space-y-2">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <p className="text-xs text-gray-500">
              Most likely taxonomic identity
              <span className="ml-2">
                <AmpliconWidget amplicon={summary.amplicon as Taxas} showIcon={false} />
              </span>
            </p>
            <ConfidenceBadge confidence={summary.confidence} />
          </div>
          {head ? (
            <h2
              className={`text-4xl font-extrabold text-gray-900 flex items-center gap-2 break-words min-w-0 ${isItalicRank(head.rank) ? 'italic' : ''}`}
            >
              {head.name}
              <a href={gbifRedirectPath(head.name)} target="_blank" rel="noopener noreferrer" title="Look up in GBIF">
                <ArrowTopRightOnSquareIcon className="w-5 h-5 text-gray-400 hover:text-primary" />
              </a>
            </h2>
          ) : (
            <h2 className="text-2xl font-bold text-gray-400">Unresolved</h2>
          )}
          {path.length > 0 && (
            <p className="text-sm text-gray-700 flex flex-wrap items-center gap-x-1.5 gap-y-1">
              {path.map((r, i) => (
                <React.Fragment key={r.rank}>
                  {i > 0 && <ChevronRightIcon className="w-3.5 h-3.5 text-gray-400 shrink-0" />}
                  <span title={RANK_LABELS[r.rank]} className={isItalicRank(r.rank) ? 'italic' : ''}>
                    {r.value}
                  </span>
                </React.Fragment>
              ))}
            </p>
          )}
          {split && (
            <div className="flex items-start gap-2 text-sm px-3 py-2 rounded-lg bg-amber-100 border border-amber-300 text-amber-950 font-medium">
              <ExclamationTriangleIcon className="w-5 h-5 shrink-0" />
              <span>
                {split.rank}-level conflict between databases: <i>{split.candidates.join(' vs. ')}</i>.
                {split.resolvedBy && ` Shown as decided by ${split.resolvedBy}.`}
              </span>
            </div>
          )}
        </div>
      </div>
      <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-4 text-sm">
        <p className="text-xs text-gray-500">Taxonomic resolution</p>
        <p className="text-2xl font-bold text-emerald-900">{depth == null ? 'Unresolved' : `${RANK_LABELS[depth]} level`}</p>
        {resolvedBy && <p className="text-xs text-gray-500">{resolvedBy}</p>}
        <progress className="progress progress-success w-full h-3 my-3" value={depth == null ? 0 : depth + 1} max={8} />
        <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5">
          {[5, 6, 7].map((r) => (
            <React.Fragment key={r}>
              <dt className="text-gray-500">{RANK_LABELS[r]}:</dt>
              <dd>{agreementAt(display, r) ?? '—'}</dd>
            </React.Fragment>
          ))}
        </dl>
      </div>
    </section>
  )
}

/** One rank of a source's ladder; truncates rather than wraps so the ladder stays on one line. */
/** [support] below the RDP cut-off fades the chip: the classifier's call at that rank is a guess. */
const RankChip: React.FC<{ label: string; value?: string | null; italic?: boolean; support?: number }> = ({
  label,
  value,
  italic,
  support
}) => {
  const name = cleanName(value)
  const weak = name != null && support != null && support < RDP_CONFIDENT
  return (
    <div className="min-w-0">
      <div className="text-[11px] text-gray-500">{label}</div>
      <div
        className={`rounded px-2 py-1.5 text-sm flex items-center justify-center gap-1 ${name ? 'bg-emerald-100 text-emerald-950 font-medium' : 'bg-gray-100 text-gray-400'} ${weak ? 'opacity-45 border border-dashed border-emerald-400' : ''}`}
        title={support != null && name ? `${name} · bootstrap support ${support.toFixed(2)}` : (name ?? undefined)}
      >
        <span className={`truncate ${italic ? 'italic' : ''}`}>{name ?? '—'}</span>
      </div>
    </div>
  )
}

const SparkBadge: React.FC<{ label: string; score?: number; primary?: boolean }> = ({ label, score, primary }) => (
  <span
    className={`badge badge-xs text-[10px] font-normal whitespace-nowrap shrink-0 not-italic ${primary ? 'badge-info' : 'badge-ghost'}`}
    title={score == null ? undefined : `SPARK score ${score.toFixed(2)}`}
  >
    {label}
  </span>
)

/** Top and alternative hit as equal, numbered rows: both are candidate identifications. */
const SpeciesHits: React.FC<{ hits: { name?: string | null; label: string; score?: number; primary?: boolean }[] }> = ({ hits }) => (
  <div className="min-w-0">
    <div className="text-[11px] text-gray-500">Species</div>
    <div className="space-y-1">
      {hits.map((hit, i) => {
        const name = cleanName(hit.name)
        return (
          <div key={hit.label} className="flex items-center gap-1.5">
            <span className="w-5 h-5 shrink-0 rounded border border-gray-300 text-[11px] flex items-center justify-center text-gray-600">
              {i + 1}
            </span>
            <div
              className={`flex-1 min-w-0 rounded px-2 py-1.5 text-sm flex items-center justify-between gap-1 ${name ? 'bg-emerald-100 text-emerald-950 font-medium' : 'bg-gray-100 text-gray-400'}`}
              title={name}
            >
              <span className="truncate italic">{name ?? '—'}</span>
              {name && <SparkBadge label={hit.label} score={hit.score} primary={hit.primary} />}
            </div>
          </div>
        )
      })}
    </div>
  </div>
)

const SourceRowView: React.FC<{ row: SourceRow; consensusOf: string[] }> = ({ row, consensusOf }) => {
  const link = recordLink(row)
  const alt = row.derived ? undefined : cleanName(row.alt?.ranks?.[SPECIES_RANK])
  const species = row.top?.ranks?.[SPECIES_RANK]
  const quality = hitQuality(row)
  const ids = referenceIds(row)
  const version = sourceVersion(row)
  return (
    <div
      className="rounded-lg border border-l-4 bg-white shadow-sm p-4 space-y-3"
      style={{ borderLeftColor: blastSourceColor(row.source) }}
    >
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <BlastSourceIcon source={row.source} />
        <div>
          <div className="text-base font-bold leading-tight">{row.source}</div>
          <div className="text-xs text-gray-500">
            {row.derived ? `Derived from ${consensusOf.join(', ')}` : blastSourceDescription(row.source)}
          </div>
        </div>
        {link && (
          <span className="text-xs">
            <ExternalLink href={link.url}>{link.label}</ExternalLink>
          </span>
        )}
        <div className="ml-auto flex flex-wrap items-center justify-end gap-1.5 text-xs">
          {quality && <span className="badge badge-sm bg-gray-50 border-gray-200 text-gray-700">{quality}</span>}
          {RANK_LABELS.some((_, r) => (rdpSupportAt(row, r) ?? 1) < RDP_CONFIDENT && cleanName(row.top?.ranks?.[r])) && (
            <span className="badge badge-sm bg-gray-50 border-gray-200 text-gray-700">faded: bootstrap support below {RDP_CONFIDENT}</span>
          )}
          {row.evidence?.binSharing && (
            <span className="badge badge-sm bg-sky-50 border-sky-200 text-sky-800">Shared BIN – multiple species</span>
          )}
        </div>
      </div>
      {(ids.length > 0 || version) && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500 -mt-1">
          {ids.map((id) => (
            <span key={id.label}>
              {id.label}{' '}
              {id.url ? (
                <a href={id.url} target="_blank" rel="noopener noreferrer" className="link font-mono text-gray-700">
                  {id.value}
                </a>
              ) : (
                <span className="font-mono text-gray-700">{id.value}</span>
              )}
            </span>
          ))}
          {version && <span className="ml-auto">{version}</span>}
        </div>
      )}
      <div className="flex items-center gap-1">
        {RANK_LABELS.map((label, i) => (
          <React.Fragment key={label}>
            {i > 0 && <ChevronRightIcon className="w-3.5 h-3.5 text-gray-400 shrink-0 mt-4" />}
            <div className={i === SPECIES_RANK ? 'flex-[2.6] min-w-0' : 'flex-1 min-w-0'}>
              {i === SPECIES_RANK && alt ? (
                <SpeciesHits
                  hits={[
                    { name: species, label: 'Top SPARK', score: row.evidence?.sparkTop, primary: true },
                    { name: alt, label: 'Alt SPARK', score: row.evidence?.sparkAlt }
                  ]}
                />
              ) : (
                <RankChip label={label} value={row.top?.ranks?.[i]} italic={isItalicRank(i)} support={rdpSupportAt(row, i)} />
              )}
            </div>
          </React.Fragment>
        ))}
      </div>
    </div>
  )
}

export const AssignmentsSection: React.FC<{ summary: SpinSummary }> = ({ summary }) => {
  const rows = sourceRows(summary)
  const references = rows.filter((r) => !r.derived).map((r) => r.source)
  return (
    <Section
      title="Taxonomic assignments"
      hint="Results from each reference database and classifier, and their consensus where the amplicon has one. Databases may report a top and an alternative SPARK."
    >
      {rows.length === 0 ? (
        <p className="text-sm text-gray-500">No taxonomy has been resolved for this SPIN.</p>
      ) : (
        <div className="space-y-2">
          {rows.map((row) => (
            <SourceRowView key={row.source} row={row} consensusOf={references} />
          ))}
        </div>
      )}
    </Section>
  )
}

/** Shown in place of scores computed over too few members to mean much, with a way to see them anyway. */
const SparseGate: React.FC<{ members: number; children: React.ReactNode }> = ({ members, children }) => {
  const [shown, setShown] = useState(false)
  if (members >= SPARSE_MEMBER_THRESHOLD || shown) return <>{children}</>
  return (
    <div className="text-sm text-gray-600 bg-gray-50 rounded p-3">
      With {members} member{members === 1 ? '' : 's'} these scores say little: a single sequence always agrees with itself.{' '}
      <button type="button" className="link link-primary" onClick={() => setShown(true)}>
        Show anyway
      </button>
    </div>
  )
}

const TONE_STYLE: Record<ScoreTone, string> = {
  good: 'bg-emerald-50 border-emerald-200 text-emerald-900',
  fair: 'bg-amber-50 border-amber-200 text-amber-900',
  poor: 'bg-rose-50 border-rose-200 text-rose-900'
}

const ScoreCard: React.FC<{
  title: string
  info: string
  words: Record<ScoreTone, string>
  toneOf: (value: number | undefined, source?: string) => ScoreTone | undefined
  bySource?: Record<string, number>
  preferred?: string
}> = ({ title, info, words, toneOf, bySource, preferred }) => {
  const h = headlineScore(bySource, preferred)
  const tone = toneOf(h.value, h.source)
  return (
    <div
      className={`rounded-xl border px-5 py-4 flex flex-col gap-3 ${tone ? TONE_STYLE[tone] : 'bg-gray-50 border-gray-200 text-gray-500'}`}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="flex items-center gap-1 text-sm font-semibold">
          {title}
          <InfoTip text={info} />
        </span>
        {tone && <span className="rounded-full bg-white/70 px-2.5 py-0.5 text-xs font-semibold">{words[tone]}</span>}
      </div>
      <div className="text-4xl font-extrabold leading-none">{formatScore(h.value)}</div>
      <div className="text-xs opacity-75 leading-snug space-y-0.5">
        {h.source && <div>from {h.source}</div>}
        {h.count > 1 && h.min != null && h.max != null && (
          <div>
            {formatScore(h.min)}–{formatScore(h.max)} across {h.count} databases
          </div>
        )}
      </div>
    </div>
  )
}

export const ScoresSection: React.FC<{ summary: SpinSummary }> = ({ summary }) => {
  const unity = summary.scores?.unityBySource ?? {}
  const reach = summary.scores?.reachBySource ?? {}
  const sources = [...new Set([...Object.keys(unity), ...Object.keys(reach)])].sort()
  const preferred = summary.display?.preferredSource
  return (
    <Section title="Unity and reach">
      {sources.length === 0 ? (
        <p className="text-sm text-gray-500">No member taxonomy available.</p>
      ) : (
        <SparseGate members={summary.memberCount}>
          <div className="grid gap-4 sm:grid-cols-2">
            <ScoreCard
              title="Unity"
              info="How uniformly this SPIN's member sequences are classified at each rank. 100% means every member has the identical ladder. Coherent: at most an even split at species. Mixed: members still agree on the order."
              words={UNITY_WORD}
              toneOf={unityTone}
              bySource={unity}
              preferred={preferred}
            />
            <ScoreCard
              title="Reach"
              info="How many ranks a member typically resolves, relative to what that database usually fills. Deep: members typically reach genus or species. Partial: family."
              words={REACH_WORD}
              toneOf={reachTone}
              bySource={reach}
              preferred={preferred}
            />
          </div>
          <details className="mt-4 text-sm">
            <summary className="cursor-pointer text-gray-600">Details per database</summary>
            <table className="table table-xs mt-2">
              <thead>
                <tr>
                  <th>Database</th>
                  <th className="text-right">Unity</th>
                  <th className="text-right">Reach</th>
                </tr>
              </thead>
              <tbody>
                {sources.map((s) => (
                  <tr key={s}>
                    <td>
                      <span className="inline-flex items-center gap-1.5">
                        <BlastSourceIcon source={s} size={16} />
                        {s}
                      </span>
                    </td>
                    <td className="text-right">{formatScore(unity[s])}</td>
                    <td className="text-right">{formatScore(reach[s])}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </details>
        </SparseGate>
      )}
    </Section>
  )
}

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div>
    <div className="text-xs text-gray-500">{label}</div>
    <div className="text-sm break-words">{children ?? '—'}</div>
  </div>
)

export const DefinitionSection: React.FC<{ summary: SpinSummary }> = ({ summary }) => (
  <Section title="SPIN definition">
    <div className="grid grid-cols-[repeat(auto-fill,minmax(10rem,1fr))] gap-4">
      <Field label="Algorithm">
        {summary.algorithmType} · {summary.algorithmVersion}
      </Field>
      <Field label="Amplicon">
        <AmpliconWidget amplicon={summary.amplicon as Taxas} showIcon={false} />
      </Field>
      <Field label="Members">
        {summary.memberCount === 0 ? (
          <span title="Every member has moved to a successor SPIN">0 · superseded</span>
        ) : (
          summary.memberCount.toLocaleString()
        )}
      </Field>
      <Field label="Derived (minted)">{formatDate(parseDate(summary.mintedAt))}</Field>
      <Field label="First created">{formatDate(parseDate(summary.createdAt))}</Field>
      <Field label="Last blasted">
        <LastBlasted at={summary.lastBlastedAt} source={summary.lastBlastSource} />
      </Field>
      <Field label="Predecessors">
        {(summary.predecessorSpinIds ?? []).length === 0 ? (
          '—'
        ) : (
          <div className="flex flex-col">
            {(summary.predecessorSpinIds ?? []).map((p) => (
              <Link key={p} to={`/spins/${p}`} className="link link-primary font-mono text-xs">
                {shortSpinId(p)}
              </Link>
            ))}
          </div>
        )}
      </Field>
    </div>
    {summary.coreSequence && (
      <div className="mt-4">
        <div className="text-xs text-gray-500 flex items-center gap-2">
          Core sequence
          <button
            type="button"
            title="Copy sequence"
            aria-label="Copy sequence"
            onClick={() => void navigator.clipboard?.writeText(summary.coreSequence!)}
          >
            <ClipboardDocumentIcon className="w-4 h-4 hover:text-primary" />
          </button>
        </div>
        <div className="font-mono text-xs break-all bg-gray-50 rounded p-2 max-h-32 overflow-auto">{summary.coreSequence}</div>
      </div>
    )}
  </Section>
)

export const BinCountriesSection: React.FC<{ summary: SpinSummary }> = ({ summary }) => {
  const bold = summary.evidence?.find((e) => e.source === 'BOLD')
  const countries = binCountries(bold?.binCountries)
  const species = binSpecies(summary.binSpecies)
  if (countries.length === 0 && species.length === 0) return null
  return (
    <Section
      title="BOLD reference BIN"
      hint="Public BOLD reference records in the BIN of this SPIN's top BOLD hit — where those specimens were collected, not where this SPIN was detected."
    >
      {species.length > 1 && (
        <p className="text-sm mb-2">
          Species in this BIN:{' '}
          {species.map((sp, i) => (
            <span key={sp.name}>
              {i > 0 && ', '}
              <i>{sp.name}</i>
              {sp.records != null && <span className="text-gray-500"> ({sp.records})</span>}
            </span>
          ))}
        </p>
      )}
      {countries.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {countries.map((c) => (
            <span key={c} className="badge badge-ghost badge-sm">
              {c}
            </span>
          ))}
        </div>
      )}
    </Section>
  )
}
